import { LayoutGroup, motion, useReducedMotion } from "framer-motion";
import {
  AtSign,
  Calendar as CalendarIcon,
  Database,
  FileText,
  Globe,
  Hash,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Paperclip,
  Users as UsersIcon,
} from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "../primitives/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "../primitives/tooltip";

/**
 * SourceRail — horizontal source picker that sits ABOVE the inline viewer
 * in each workspace hub (Documents / Conversations / Calendar / Finance).
 *
 * UX contract (SOTA May 2026, ref. Linear/Notion AI/Perplexity):
 *
 *   1. Renders only when ``items.length >= 2`` — a single-chip rail is
 *      visual debt (Vercel v0 pattern post-Mar 2026). Caller must NOT
 *      mount it for 0/1 items.
 *   2. Shows up to ``maxVisible`` (default 5) chips inline; the rest
 *      collapse behind a ``+N`` Radix Popover sorted by relevance desc.
 *   3. The active chip carries a Framer ``layoutId`` accent that morphs
 *      between chips for a shared-element transition.
 *   4. Cross-type indicator (chip's ``destinationTab`` differs from
 *      ``currentTab``) — corner-dot with high-contrast white ring +
 *      ``↗`` prefix in the aria-label so screen readers signal the jump.
 *   5. Hover delay 400ms then tooltip (NOT a preview — Linear's lesson —
 *      preview-on-hover causes accidental fetches). Respects
 *      ``prefers-reduced-motion`` (no animation, no spring).
 *   6. Keyboard: ``role="toolbar"`` semantics, native ``button`` focus
 *      cycle via Tab; Enter/Space commits.
 *
 * TooltipProvider scoping: this component does NOT mount its own
 * ``TooltipProvider``. The provider MUST be hoisted to the app root
 * (Radix UI requirement for delayDuration coordination). The Ando
 * authed-root layout mounts one provider with ``delayDuration={400}``.
 *
 * Presentational only. Wire-up (resolve items from a chat message id,
 * choose destination tab from source_type, navigate) lives in the feature
 * consumer (``apps/client/src/features/sources/SourceRailSurface``).
 */

/** Coarse source type — single source of truth shared with MessageCitation. */
export type SourceRailKind =
  | "document"
  | "email"
  | "chat"
  | "calendar"
  | "database"
  | "web"
  | "other";

/** A single rail entry. ``destinationTab`` drives the cross-type indicator. */
export interface SourceRailItem {
  /** Stable id (``doc_id`` / ``message_id`` / ``event_id`` depending on kind). */
  readonly id: string;
  /** Source kind — picks the icon + cross-type accent tone. */
  readonly kind: SourceRailKind;
  /** Display title (filename, subject, event title…). */
  readonly title: string;
  /**
   * Relevance score in ``[0, 1]`` — drives the right-side micro-bar fill.
   * BE convention: the array is already sorted by this desc; we re-sort
   * defensively at the consumer layer.
   */
  readonly relevance: number;
  /**
   * Workspace tab where clicking this chip would navigate. When this
   * differs from ``currentTab`` the chip shows a cross-type corner-dot.
   * Pass-through string so the rail stays decoupled from the workspace
   * router enum (``@ando/core`` ``WorkspaceTab``).
   */
  readonly destinationTab: string;
  /** Optional 1-line snippet for the hover tooltip. */
  readonly snippet?: string;
  /** Optional ISO timestamp for the tooltip's relative-time line. */
  readonly timestamp?: string;
  /**
   * Original URL for ``kind === "web"`` items. Drives the favicon +
   * domain display in the hover popover (Linear / Perplexity pattern).
   * Dropped for non-web kinds so the tooltip stays compact.
   */
  readonly url?: string;
  /**
   * Optional rich metadata bundle for non-web kinds — drives the
   * dedicated header in the hover popover. Lifted out of the top-level
   * interface to keep the common shape lean: web sources only set
   * ``url``, email sets ``email``, chat sets ``chat``. The consumer
   * (``SourceRailSurface``) maps the wire ``AndoSource`` shape into the
   * minimal subset the UI needs — full sender/recipient lists stay in
   * the focused viewer, not the chip tooltip.
   */
  readonly email?: EmailSourceMeta;
  readonly chat?: ChatSourceMeta;
}

/** Email source preview metadata. All fields optional — fail-soft render. */
export interface EmailSourceMeta {
  /** Display name (``"Mario Rossi"``) — falls back to ``sender`` if absent. */
  readonly senderName?: string;
  /** Raw email address (``"mario@acme.com"``) — drives the favicon domain. */
  readonly sender?: string;
  /** First recipient display, e.g. ``"a me"`` / ``"Team Marketing"``. */
  readonly to?: string;
  /** ISO timestamp — formatted as relative time ("2 ore fa") in the popover. */
  readonly sentAt?: string;
  /** Number of attachments — drives the paperclip badge. */
  readonly attachmentsCount?: number;
}

/** Chat thread source preview metadata. */
export interface ChatSourceMeta {
  /** Provider key (``"teams" | "slack" | "google_chat" | …``). */
  readonly provider?: string;
  /** Microsoft Teams: parent team name. */
  readonly teamName?: string;
  /** Teams / Slack: channel name. */
  readonly channelName?: string;
  /** Google Chat: space name. */
  readonly spaceName?: string;
  /** Authoring user display (Teams/Slack/GChat author of the cited msg). */
  readonly senderName?: string;
  /** Other participants (capped to ≤3 in the consumer for UI compactness). */
  readonly participantNames?: readonly string[];
  /** Last message timestamp — relative time in the popover. */
  readonly lastMessageAt?: string;
}

/**
 * Visual variant for the rail.
 *
 *   - ``"default"`` — the rich workspace-hub rail (48px h, bordered tab
 *     strip, relevance micro-bar, framer ``layoutId`` active accent).
 *     Used by ``FocusedViewLayout`` above the inline viewer.
 *   - ``"footnote"`` — demoted in-chat citation strip rendered UNDER
 *     each agent bubble. Smaller text, no chrome, no border, no
 *     relevance micro-bar, no active-accent shared layout — pure
 *     "footnote" affordance so neighbouring actionable chips (Grafico,
 *     etc.) keep visual priority (June 14, 2026 — explicit user
 *     feedback: "il chip si vede poco" because source citations were
 *     visually indistinguishable from the actionable chips next to
 *     them).
 */
export type SourceRailVariant = "default" | "footnote";

export interface SourceRailProps {
  readonly items: readonly SourceRailItem[];
  /** Id of the chip that is currently active (viewer is rendering it). */
  readonly activeId: string | null;
  /** Tab the rail is mounted inside — drives the cross-type indicator. */
  readonly currentTab: string;
  /** Click handler — receives the full item so the consumer can navigate. */
  readonly onSelect: (item: SourceRailItem) => void;
  /** Maximum chips rendered inline before the ``+N`` popover. Default 5. */
  readonly maxVisible?: number;
  /** Optional className passthrough — caller controls horizontal inset. */
  readonly className?: string;
  /**
   * Visual variant. Defaults to ``"default"`` so existing V1/V2
   * workspace-hub callers (``FocusedViewLayout``) keep the rich rail.
   * Pass ``"footnote"`` to demote the rail to a foot-of-message
   * citation strip (used by the in-chat ``SourceRailSurface`` mount
   * under each agent bubble).
   */
  readonly variant?: SourceRailVariant;
}

const ICON_BY_KIND: Record<
  SourceRailKind,
  React.ComponentType<{ className?: string; strokeWidth?: number }>
> = {
  document: FileText,
  email: Mail,
  chat: MessageSquare,
  calendar: CalendarIcon,
  database: Database,
  web: Globe,
  other: FileText,
};

/**
 * Italian display label for the source kind — used inside the cross-type
 * tooltip ("Apre nel tab Conversazioni"). Keep in sync with the workspace
 * tab labels in ``WorkspaceTabs`` so the microcopy reads consistently.
 */
const KIND_LABEL: Record<SourceRailKind, string> = {
  document: "Documento",
  email: "Email",
  chat: "Chat",
  calendar: "Evento",
  database: "Database",
  web: "Web",
  other: "Fonte",
};

export const SourceRail: React.FC<SourceRailProps> = ({
  items,
  activeId,
  currentTab,
  onSelect,
  maxVisible = 5,
  className,
  variant = "default",
}) => {
  // Hooks ALWAYS run before any early-return — Rules of Hooks.
  // Sorting a ≤12-element array is O(n log n) and cheaper than the
  // memo machinery, but the memo also keeps reference identity stable
  // so children memoise cleanly.
  const ranked = React.useMemo(() => [...items].sort((a, b) => b.relevance - a.relevance), [items]);

  // Defensive guard — the footnote variant is intentionally visible
  // for a SINGLE source too (the surface already renders the "Fonti"
  // micro-label above so a single chip still reads as a footnote, not
  // visual debt). The default workspace-hub rail keeps its ≥2 floor
  // because a single rich chip below the SubTabBar IS visual debt.
  const minItems = variant === "footnote" ? 1 : 2;
  if (items.length < minItems) return null;

  const visible = ranked.slice(0, maxVisible);
  const overflow = ranked.slice(maxVisible);

  return (
    <div
      role="toolbar"
      aria-label="Fonti della risposta — naviga tra le alternative"
      aria-orientation="horizontal"
      data-variant={variant}
      className={cn(
        // SubTabBar sits above (44px); rail height 48px keeps the
        // double-bar vertical rhythm at a comfortable 92px total.
        //
        // June 4, 2026 — horizontal scroll when chips overflow the
        // container (was breaking the chat layout: chips spilled past
        // the right edge of the message column, pushing the popover
        // off-screen). ``flex-nowrap`` keeps them on one line,
        // ``overflow-x-auto`` lets the user scroll, and
        // ``[scrollbar-width:thin]`` + ``::-webkit-scrollbar`` polish
        // the macOS/Chrome rendering so the chrome reads as Linear/
        // Vercel "soft" scrollbars (orange tint at rest, ink at hover).
        variant === "default"
          ? "relative flex h-12 flex-nowrap items-center gap-2 border-hairline border-b bg-transparent px-5"
          : // Footnote variant — no border, no fixed height, no
            // horizontal inset. The wrapping surface owns the spacing
            // (it sits flush under the agent bubble's last line).
            // ``gap-1`` keeps the chips tighter than the rich rail so
            // a 3-chip row reads as ONE footnote phrase, not three
            // pills competing for attention.
            "relative flex flex-nowrap items-center gap-1 bg-transparent",
        "overflow-x-auto overflow-y-hidden",
        "[scrollbar-width:thin]",
        "[&::-webkit-scrollbar]:h-1.5",
        "[&::-webkit-scrollbar-track]:bg-transparent",
        "[&::-webkit-scrollbar-thumb]:rounded-full",
        "[&::-webkit-scrollbar-thumb]:bg-ink-3/15",
        "[&::-webkit-scrollbar-thumb:hover]:bg-ink-3/35",
        className,
      )}
    >
      {visible.map((item, idx) => (
        <React.Fragment key={item.id}>
          {/* Footnote variant — thin hairline divider between chips
              so the row reads as comma-separated citation list, not
              a row of equal-weight pills. Skipped before the first
              chip (no leading divider). */}
          {variant === "footnote" && idx > 0 ? (
            <span aria-hidden className="h-px w-3 shrink-0 bg-ink-4/30" />
          ) : null}
          <SourceChip
            item={item}
            active={item.id === activeId}
            crossType={item.destinationTab !== currentTab}
            onSelect={onSelect}
            variant={variant}
          />
        </React.Fragment>
      ))}
      {overflow.length > 0 ? (
        <OverflowMenu
          items={overflow}
          currentTab={currentTab}
          activeId={activeId}
          onSelect={onSelect}
          variant={variant}
        />
      ) : null}
    </div>
  );
};
SourceRail.displayName = "SourceRail";

// ── Web source header (popover) ──────────────────────────────────────────

/**
 * Compact favicon + domain badge rendered at the top of the web-source
 * hover popover. Mirrors the Perplexity / ChatGPT search-result UX:
 * favicon (Google S2 service, no API key, CDN cached) + bare hostname
 * so the user can grok the source at a glance before reading the title.
 *
 * Fail-soft: when the URL doesn't parse (BE wire bug or relative URL)
 * we drop the favicon and just print the raw string so the popover
 * still surfaces a "WEB" badge instead of crashing.
 */
const WebSourceHeader: React.FC<{ url: string }> = React.memo(function WebSourceHeader({ url }) {
  const parsed = React.useMemo(() => {
    try {
      const u = new URL(url);
      return { hostname: u.hostname.replace(/^www\./, ""), origin: u.origin };
    } catch {
      return null;
    }
  }, [url]);
  const [iconBroken, setIconBroken] = React.useState(false);

  if (!parsed) {
    return <p className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.08em]">Web</p>;
  }

  return (
    <div className="flex items-center gap-1.5">
      {!iconBroken ? (
        /* Google S2 favicon endpoint — public + CDN cached. The 32px
             icon is rendered at 14px for crispness on hi-DPI screens. */
        <img
          src={`https://www.google.com/s2/favicons?sz=32&domain=${parsed.hostname}`}
          alt=""
          aria-hidden
          width={14}
          height={14}
          loading="lazy"
          onError={() => setIconBroken(true)}
          className="h-3.5 w-3.5 shrink-0 rounded-sm"
        />
      ) : (
        <span
          aria-hidden
          className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm bg-ink-4/15 text-[9px] text-ink-3"
        >
          {parsed.hostname.charAt(0).toUpperCase()}
        </span>
      )}
      <span className="truncate font-medium text-[11px] text-ink-2">{parsed.hostname}</span>
      <span className="shrink-0 rounded-full bg-ink-4/15 px-1.5 py-0.5 font-semibold text-[9px] text-ink-3 uppercase tracking-[0.06em]">
        Web
      </span>
    </div>
  );
});

// ── Email source header (popover) ────────────────────────────────────────

/** Cheap relative-time formatter — keeps the popover dep-free. */
function formatRelative(iso: string | undefined): string | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const diff = Date.now() - ts;
  if (diff < 0) return "ora";
  const min = Math.round(diff / 60_000);
  if (min < 1) return "ora";
  if (min < 60) return `${min} min fa`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ${h === 1 ? "ora" : "ore"} fa`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} ${d === 1 ? "giorno" : "giorni"} fa`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w} ${w === 1 ? "settimana" : "settimane"} fa`;
  const m = Math.round(d / 30);
  if (m < 12) return `${m} ${m === 1 ? "mese" : "mesi"} fa`;
  const y = Math.round(d / 365);
  return `${y} ${y === 1 ? "anno" : "anni"} fa`;
}

/**
 * Email source preview header — sender avatar (initials) + display name +
 * sender domain pill. Mirrors the Gmail / Outlook search-result card.
 *
 * Domain pill ("@acme.com") is what the user actually scans for trust
 * ("è un'email vera del cliente?") — way more useful than the raw "EMAIL"
 * label we shipped before. Initials avatar uses ink-4 background so it
 * stays calm in the card chrome (no random user colors).
 */
const EmailSourceHeader: React.FC<{ meta: EmailSourceMeta }> = React.memo(
  function EmailSourceHeader({ meta }) {
    const display = meta.senderName?.trim() || meta.sender?.trim() || "Email";
    const domain = React.useMemo(() => {
      const at = meta.sender?.lastIndexOf("@");
      return at !== undefined && at >= 0 ? meta.sender!.slice(at + 1) : null;
    }, [meta.sender]);
    const initials = React.useMemo(() => {
      const parts = display.split(/\s+/).filter(Boolean);
      const a = parts[0]?.[0] ?? "";
      const b = parts.length > 1 ? parts[parts.length - 1]![0] : "";
      return (a + b).toUpperCase() || "@";
    }, [display]);

    return (
      <div className="flex items-center gap-1.5">
        <span
          aria-hidden
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-ink-4/20 font-semibold text-[8px] text-ink-2 leading-none"
        >
          {initials}
        </span>
        <span className="truncate font-medium text-[11px] text-ink-2">{display}</span>
        {domain ? (
          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-ink-4/15 px-1.5 py-0.5 font-medium text-[9px] text-ink-3">
            <AtSign className="h-2.5 w-2.5" strokeWidth={2.5} />
            {domain}
          </span>
        ) : (
          <span className="shrink-0 rounded-full bg-ink-4/15 px-1.5 py-0.5 font-semibold text-[9px] text-ink-3 uppercase tracking-[0.06em]">
            Email
          </span>
        )}
      </div>
    );
  },
);

// ── Chat source header (popover) ─────────────────────────────────────────

/** Brand glyph for chat providers — coloured rounded badge. */
const PROVIDER_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  teams: { label: "T", bg: "bg-[#5559b8]/20", fg: "text-[#4b53bc]" },
  ms_teams: { label: "T", bg: "bg-[#5559b8]/20", fg: "text-[#4b53bc]" },
  slack: { label: "S", bg: "bg-[#ecb22e]/25", fg: "text-[#b48816]" },
  google_chat: { label: "G", bg: "bg-[#1a73e8]/15", fg: "text-[#1a73e8]" },
  gchat: { label: "G", bg: "bg-[#1a73e8]/15", fg: "text-[#1a73e8]" },
};

const PROVIDER_LABEL: Record<string, string> = {
  teams: "Microsoft Teams",
  ms_teams: "Microsoft Teams",
  slack: "Slack",
  google_chat: "Google Chat",
  gchat: "Google Chat",
};

/**
 * Chat source preview header — provider glyph + breadcrumb (Team/Space ›
 * Channel) + sender. Replaces the plain "CHAT" label for Teams / Slack /
 * Google Chat citations so the user can grok provider, room, and author
 * at a glance before reading the title (which often duplicates the
 * channel name anyway).
 *
 * Fail-soft: missing provider → generic chat-bubble glyph; missing
 * breadcrumb fields → provider label only; missing senderName → omit
 * the bottom line.
 */
const ChatSourceHeader: React.FC<{ meta: ChatSourceMeta }> = React.memo(function ChatSourceHeader({
  meta,
}) {
  const providerKey = meta.provider?.toLowerCase() ?? "";
  const badge = PROVIDER_BADGE[providerKey];
  const providerLabel = PROVIDER_LABEL[providerKey] ?? "Chat";

  // Breadcrumb: Teams ships ``team_name › channel_name``, Slack ships
  // only ``channel_name`` (workspace lives outside the chunk), Google
  // Chat ships ``space_name``. Render whichever is present.
  const crumbs: string[] = [];
  if (meta.teamName?.trim()) crumbs.push(meta.teamName.trim());
  if (meta.channelName?.trim()) crumbs.push(`#${meta.channelName.trim()}`);
  else if (meta.spaceName?.trim()) crumbs.push(meta.spaceName.trim());

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        {badge ? (
          <span
            aria-hidden
            className={cn(
              "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm font-bold text-[9px] leading-none",
              badge.bg,
              badge.fg,
            )}
          >
            {badge.label}
          </span>
        ) : (
          <MessageSquare className="h-3.5 w-3.5 shrink-0 text-ink-3" strokeWidth={2} />
        )}
        <span className="truncate font-medium text-[11px] text-ink-2">{providerLabel}</span>
        <span className="shrink-0 rounded-full bg-ink-4/15 px-1.5 py-0.5 font-semibold text-[9px] text-ink-3 uppercase tracking-[0.06em]">
          Chat
        </span>
      </div>
      {crumbs.length > 0 ? (
        <p className="flex items-center gap-1 text-[10.5px] text-ink-3">
          <Hash className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
          <span className="truncate">{crumbs.join(" › ")}</span>
        </p>
      ) : null}
    </div>
  );
});

/**
 * Email meta strip — second-row micro-context: ``"📩 a me · 2 ore fa · 📎 2"``.
 * Lives between the snippet and the relevance footer so the email card
 * gives the user the same "is this for me?" answer they'd get from a
 * Gmail list row, without committing to the full focused viewer.
 */
const EmailMetaStrip: React.FC<{ meta: EmailSourceMeta }> = React.memo(function EmailMetaStrip({
  meta,
}) {
  const when = formatRelative(meta.sentAt);
  const parts: React.ReactNode[] = [];
  if (meta.to?.trim()) {
    parts.push(
      <span key="to" className="truncate">
        a {meta.to.trim()}
      </span>,
    );
  }
  if (when) {
    parts.push(
      <span key="when" className="shrink-0">
        {when}
      </span>,
    );
  }
  if (meta.attachmentsCount && meta.attachmentsCount > 0) {
    parts.push(
      <span key="att" className="inline-flex shrink-0 items-center gap-0.5">
        <Paperclip className="h-2.5 w-2.5" strokeWidth={2.5} />
        {meta.attachmentsCount}
      </span>,
    );
  }
  if (parts.length === 0) return null;
  return (
    <p className="flex items-center gap-1.5 text-[10.5px] text-ink-3">
      {parts.map((node, i) => (
        <React.Fragment key={i}>
          {i > 0 ? (
            <span aria-hidden className="shrink-0 text-ink-4">
              ·
            </span>
          ) : null}
          {node}
        </React.Fragment>
      ))}
    </p>
  );
});

/**
 * Chat meta strip — second-row micro-context for chat citations:
 * ``"👥 Mario Rossi · 2 ore fa"``. Author + relative time only — the
 * provider/breadcrumb already live in the header above, no need to
 * re-render them here.
 */
const ChatMetaStrip: React.FC<{ meta: ChatSourceMeta }> = React.memo(function ChatMetaStrip({
  meta,
}) {
  const when = formatRelative(meta.lastMessageAt);
  const author = meta.senderName?.trim();
  // Cap participants to 2 to avoid wrapping — full list lives in the
  // focused viewer.
  const others = (meta.participantNames ?? [])
    .filter((p) => typeof p === "string" && p.trim().length > 0)
    .filter((p) => p !== meta.senderName)
    .slice(0, 2);

  const parts: React.ReactNode[] = [];
  if (author) {
    parts.push(
      <span key="who" className="truncate">
        {author}
      </span>,
    );
  } else if (others.length > 0) {
    parts.push(
      <span key="who" className="inline-flex items-center gap-0.5 truncate">
        <UsersIcon className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
        {others.join(", ")}
      </span>,
    );
  }
  if (when) {
    parts.push(
      <span key="when" className="shrink-0">
        {when}
      </span>,
    );
  }
  if (parts.length === 0) return null;
  return (
    <p className="flex items-center gap-1.5 text-[10.5px] text-ink-3">
      {parts.map((node, i) => (
        <React.Fragment key={i}>
          {i > 0 ? (
            <span aria-hidden className="shrink-0 text-ink-4">
              ·
            </span>
          ) : null}
          {node}
        </React.Fragment>
      ))}
    </p>
  );
});

// ── Single chip ──────────────────────────────────────────────────────────

interface SourceChipProps {
  readonly item: SourceRailItem;
  readonly active: boolean;
  readonly crossType: boolean;
  readonly onSelect: (item: SourceRailItem) => void;
  readonly variant: SourceRailVariant;
}

const SourceChip: React.FC<SourceChipProps> = React.memo(function SourceChip({
  item,
  active,
  crossType,
  onSelect,
  variant,
}) {
  const reducedMotion = useReducedMotion();
  const Icon = ICON_BY_KIND[item.kind];
  const relevancePct = Math.round(clamp01(item.relevance) * 100);
  // Screen readers get the ↗ glyph for cross-type chips — VoiceOver
  // speaks "diagonal arrow" so the tab-jump cue is audible.
  const ariaPrefix = crossType ? "↗ " : "";
  const ariaLabel = crossType
    ? `${ariaPrefix}${item.title} · apre ${KIND_LABEL[item.kind]} nel tab ${item.destinationTab}`
    : item.title;

  // Stable click handler — captures ``item`` in closure but the
  // ``onSelect`` ref comes from the (memoised) parent, so the function
  // identity changes only when item or onSelect changes.
  const handleClick = React.useCallback(() => {
    onSelect(item);
  }, [item, onSelect]);

  const isFootnote = variant === "footnote";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          aria-label={ariaLabel}
          aria-pressed={active}
          data-kind={item.kind}
          data-cross-type={crossType ? "true" : undefined}
          data-variant={variant}
          className={cn(
            // Base layout — content-sized with min/max guardrails so 3-12
            // chips compose nicely without horizontal scroll.
            "group relative inline-flex shrink-0 items-center text-left",
            "transition-colors duration-fast ease-smooth",
            isFootnote
              ? // Footnote chrome — pure text affordance. No background,
                // no border, no padding chrome; just inline-text + icon.
                // Tightly clamped width so a 3-chip row reads as a
                // citation list, not three buttons. Hover drops the
                // underline (footnote convention).
                cn(
                  "h-5 max-w-[160px] gap-1 rounded-sm px-0.5",
                  "border border-transparent bg-transparent",
                  active
                    ? "text-ink-1 underline underline-offset-2"
                    : "text-ink-3 hover:text-ink-1 hover:underline hover:underline-offset-2",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1 focus-visible:ring-offset-cream",
                )
              : cn(
                  "h-9 min-w-[140px] max-w-[200px] gap-2 rounded-md px-2.5",
                  // Active vs idle — ghost when idle (Linear pattern); subtle
                  // raised + primary ring + layout-animated accent when active.
                  active
                    ? "bg-white/85 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-ando-orange/40"
                    : "bg-transparent hover:bg-white/55",
                  // Focus ring — never on :focus alone (May 2026 a11y).
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1 focus-visible:ring-offset-cream",
                ),
          )}
        >
          {/* Active left-accent — animates between chips via shared
              ``layoutId`` (set on the ``LayoutGroup`` mounted by
              ``SourceRailLayoutScope`` upstream). Respects reduced
              motion. Skipped entirely in the footnote variant — a
              left-accent bar would re-introduce visible chrome that
              defeats the demotion. */}
          {active && !isFootnote ? (
            <motion.span
              layoutId="source-rail-active-accent"
              transition={
                reducedMotion ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 38 }
              }
              aria-hidden
              className="absolute top-1.5 bottom-1.5 left-0 w-[2px] rounded-full bg-ando-orange"
            />
          ) : null}

          {isFootnote ? (
            // Footnote variant — bare 10px icon, no badge background.
            // The icon reads as a leading glyph for the citation
            // label, not a kind-tag pill.
            <Icon
              aria-hidden
              className={cn(
                "h-2.5 w-2.5 shrink-0",
                active ? "text-ink-2" : "text-ink-3 group-hover:text-ink-2",
              )}
              strokeWidth={2}
            />
          ) : (
            <span
              aria-hidden
              className={cn(
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors",
                active
                  ? "bg-ando-orange/10 text-ando-orange"
                  : "bg-black/5 text-ink-3 group-hover:text-ink-2",
              )}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
            </span>
          )}

          <span
            className={cn(
              "min-w-0 flex-1 truncate",
              isFootnote
                ? cn(
                    "font-normal text-[10px] leading-none",
                    active ? "text-ink-1" : "text-ink-3 group-hover:text-ink-1",
                  )
                : cn(
                    "font-medium text-[12px]",
                    active ? "text-ink-1" : "text-ink-2 group-hover:text-ink-1",
                  ),
            )}
          >
            {item.title}
          </span>

          {/* Right-side micro-bar — 24×3px, fill scaled to relevance.
              No percentage label (Notion AI Q1 2026 redesign dropped
              them — text-noisy in a rail of chips). Width transitions
              smoothly when the relevance updates (rare, but avoids
              jerk on tests that mutate scores). Footnote variant
              drops the bar entirely — relevance is a power-user
              affordance and the bar re-introduces visible chrome. */}
          {!isFootnote ? (
            <span
              aria-hidden
              className="relative ml-1 h-[3px] w-6 shrink-0 overflow-hidden rounded-full bg-ando-orange/10"
            >
              <span
                className="block h-full rounded-full bg-ando-orange transition-[width] duration-fast ease-smooth"
                style={{ width: `${relevancePct}%` }}
              />
            </span>
          ) : null}

          {/* Cross-type corner dot — high-contrast white ring so the dot
              stays visible on cream background. Signals "this chip jumps
              tab". Footnote variant skips the dot too — the underline
              hover state + the tooltip already telegraph the click
              affordance, and a corner dot would re-add chrome. */}
          {crossType && !isFootnote ? (
            <span
              aria-hidden
              className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-ando-orange ring-2 ring-white"
            />
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        sideOffset={6}
        // June 4, 2026 — promote the source preview to a card-style
        // popover (Perplexity / Linear pattern). Pure light surface +
        // ink-1 prose + larger min-width so titles + snippets don't
        // wrap into one-word lines. The hover card stays inside
        // ``TooltipContent`` (Radix portal) so it still escapes the
        // chat container z-stack, but the styling matches the Ando
        // card chrome instead of the dark default.
        className={cn(
          "w-[320px] max-w-[min(360px,calc(100vw-2rem))]",
          "rounded-xl border border-hairline bg-white p-3",
          "text-ink-1 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_28px_-12px_rgba(0,0,0,0.18)]",
        )}
      >
        <div className="flex flex-col gap-1.5">
          {/* Header row — kind-specific:
              - web   → favicon (Google S2) + bare hostname + WEB badge
              - email → sender initials + display name + @domain pill
              - chat  → provider glyph (T/S/G) + label + breadcrumb
              - other → fall back to the plain uppercase kind label */}
          {item.kind === "web" && item.url ? (
            <WebSourceHeader url={item.url} />
          ) : item.kind === "email" && item.email ? (
            <EmailSourceHeader meta={item.email} />
          ) : item.kind === "chat" && item.chat ? (
            <ChatSourceHeader meta={item.chat} />
          ) : (
            <p className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.08em]">
              {KIND_LABEL[item.kind]}
            </p>
          )}

          {/* Title — larger + ink-1, two-line clamp so very long titles
              don't push the card past the snippet. */}
          <p className="line-clamp-2 font-semibold text-[13px] text-ink-1 leading-snug">
            {item.title}
          </p>

          {/* Snippet — soft ink-2 with three-line clamp for breathing
              room. Empty snippet → render an em-dash placeholder so the
              card's vertical rhythm stays consistent. */}
          {item.snippet ? (
            <p className="line-clamp-3 text-[12px] text-ink-2 leading-relaxed">{item.snippet}</p>
          ) : (
            <p className="text-[12px] text-ink-3 italic">Nessuna anteprima disponibile.</p>
          )}

          {/* Meta strip — kind-specific micro-context above the footer.
              Email: sender direction + sent_at + attachment count.
              Chat:  author + last_message_at.
              Other: nothing (footer renders directly). */}
          {item.kind === "email" && item.email ? <EmailMetaStrip meta={item.email} /> : null}
          {item.kind === "chat" && item.chat ? <ChatMetaStrip meta={item.chat} /> : null}

          {/* Footer — relevance pill (left) + cross-type CTA (right).
              Relevance reads as a colored pill instead of a plaintext
              line so the eye can scan a column of these without
              reparsing percentages. */}
          <div className="mt-1 flex items-center justify-between gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold text-[10px]",
                relevancePct >= 70
                  ? "bg-ando-orange/15 text-ando-orange"
                  : relevancePct >= 40
                    ? "bg-ando-orange/10 text-ando-orange/85"
                    : "bg-ink-4/15 text-ink-3",
              )}
            >
              {relevancePct}% rilevanza
            </span>
            {crossType ? (
              <span className="inline-flex items-center gap-1 font-medium text-[11px] text-ando-orange">
                ↗ Apre in {item.destinationTab}
              </span>
            ) : null}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
});

// ── Overflow popover (+N more) ───────────────────────────────────────────

interface OverflowMenuProps {
  readonly items: readonly SourceRailItem[];
  readonly currentTab: string;
  readonly activeId: string | null;
  readonly onSelect: (item: SourceRailItem) => void;
  readonly variant: SourceRailVariant;
}

const OverflowMenu: React.FC<OverflowMenuProps> = ({
  items,
  currentTab,
  activeId,
  onSelect,
  variant,
}) => {
  const [open, setOpen] = React.useState(false);
  const isFootnote = variant === "footnote";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Altre ${items.length} fonti`}
          data-variant={variant}
          className={cn(
            isFootnote
              ? cn(
                  "inline-flex h-5 items-center gap-0.5 rounded-sm px-0.5",
                  "bg-transparent text-[10px] text-ink-3 transition-colors",
                  "hover:text-ink-1 hover:underline hover:underline-offset-2",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
                )
              : cn(
                  "ml-auto inline-flex h-9 items-center gap-1.5 rounded-md px-2.5",
                  "bg-transparent text-ink-3 transition-colors hover:bg-white/55 hover:text-ink-1",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                ),
          )}
        >
          <MoreHorizontal
            className={isFootnote ? "h-2.5 w-2.5" : "h-3.5 w-3.5"}
            strokeWidth={isFootnote ? 2 : 1.75}
            aria-hidden
          />
          <span
            className={cn(
              "tabular-nums",
              isFootnote ? "font-normal text-[10px]" : "font-medium text-[12px]",
            )}
          >
            +{items.length}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-1">
        <ul className="flex flex-col" role="menu">
          {items.map((item) => {
            const Icon = ICON_BY_KIND[item.kind];
            const isActive = item.id === activeId;
            const crossType = item.destinationTab !== currentTab;
            return (
              <li key={item.id} role="presentation">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onSelect(item);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left",
                    "transition-colors hover:bg-cream-50",
                    "focus-visible:bg-cream-50 focus-visible:outline-none",
                    isActive && "bg-ando-orange/10 text-ink-1",
                  )}
                >
                  <span
                    aria-hidden
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-black/5 text-ink-3"
                  >
                    <Icon className="h-3 w-3" strokeWidth={1.75} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-[12px] text-ink-1">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block text-[10px] text-ink-3 tabular-nums">
                      {KIND_LABEL[item.kind]}
                      {crossType ? ` · ↗ ${item.destinationTab}` : ""}
                      {" · "}
                      {Math.round(clamp01(item.relevance) * 100)}%
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
OverflowMenu.displayName = "OverflowMenu";

// ── Helpers ──────────────────────────────────────────────────────────────

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * Shared-layout wrapper — caller wraps both the SourceRail AND the inline
 * viewer container in this so the active-chip accent + viewer fade share
 * a single Framer ``LayoutGroup``. Pure pass-through; exists so consumers
 * don't have to import framer-motion just for the LayoutGroup symbol.
 *
 * Use:
 *
 * ```tsx
 * <SourceRailLayoutScope>
 *   <SourceRail items={...} ... />
 *   <ViewerWithAnimatePresence keyedBy={activeId}>{viewer}</...>
 * </SourceRailLayoutScope>
 * ```
 */
export const SourceRailLayoutScope: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <LayoutGroup>{children}</LayoutGroup>
);
SourceRailLayoutScope.displayName = "SourceRailLayoutScope";
